import { FC, useMemo } from 'react'
import cn from 'classnames'
import { CRM_INTEGRATION_CHAT_PROVIDERS } from '@pages/Admin/CRM/components/PhoneIntegrationButtons/integrations'
import type { CrmIntegrationChatAvailabilityMap } from '@pages/Admin/CRM/utils/crmIntegrationChatAvailability'
import classes from './IntegrationChatDock.module.css'

type IntegrationChatProviderConfig =
  (typeof CRM_INTEGRATION_CHAT_PROVIDERS)[number]

/** Сколько иконок помещается в pill без сильного сжатия. */
const MAX_LAUNCHER_PILL_ICONS = 4

const getConnectedProviders = (
  providers: CrmIntegrationChatAvailabilityMap,
): IntegrationChatProviderConfig[] =>
  CRM_INTEGRATION_CHAT_PROVIDERS.filter(
    ({ provider }) => providers[provider].isConnected,
  )

type IntegrationChatDockLauncherProvidersProps = {
  providers: CrmIntegrationChatAvailabilityMap
}

const IntegrationChatDockLauncherProviders: FC<
  IntegrationChatDockLauncherProvidersProps
> = ({ providers }) => {
  const connectedProviders = useMemo(
    () => getConnectedProviders(providers),
    [providers],
  )

  const { visibleProviders, overflowCount, isEmpty } = useMemo(() => {
    const count = connectedProviders.length

    if (count === 0) {
      return {
        visibleProviders: CRM_INTEGRATION_CHAT_PROVIDERS,
        overflowCount: 0,
        isEmpty: true,
      }
    }

    if (count <= MAX_LAUNCHER_PILL_ICONS) {
      return {
        visibleProviders: connectedProviders,
        overflowCount: 0,
        isEmpty: false,
      }
    }

    return {
      visibleProviders: connectedProviders.slice(0, MAX_LAUNCHER_PILL_ICONS - 1),
      overflowCount: count - (MAX_LAUNCHER_PILL_ICONS - 1),
      isEmpty: false,
    }
  }, [connectedProviders])

  return (
    <span
      className={cn(classes.launcherPillIcons, {
        [classes.launcherPillIcons_empty]: isEmpty,
      })}
      aria-hidden
    >
      {visibleProviders.map(({ provider, icon: Icon, color, label }) => (
        <span
          key={provider}
          className={cn(classes.launcherProviderBubble, {
            [classes.launcherProviderBubble_placeholder]: isEmpty,
          })}
          style={{ color }}
          title={isEmpty ? undefined : label}
        >
          <Icon className={classes.launcherProviderIcon} />
        </span>
      ))}
      {overflowCount > 0 ? (
        <span className={classes.launcherPillOverflow}>+{overflowCount}</span>
      ) : null}
    </span>
  )
}

export default IntegrationChatDockLauncherProviders
